// Engancha los eventos del navegador que dicen si el usuario está mirando la app, y los reporta
// al backend. Se monta UNA sola vez, en App.tsx.
// Spec: docs/2026-08-31-push-al-terminar-generacion-spec.md
//
// Qué reporta:
//   - al montar y cada vez que cambia "estar en Atlas" (pestaña visible Y con foco, ver
//     lib/mirando) → el estado real de la pestaña. Con sólo "visible", quien dejaba Atlas a la
//     vista y se iba a otra app contaba como mirando y no le llegaba el aviso.
//   - en `pagehide` → "me fui" (con keepalive, para que llegue aunque se cierre)
//   - cada 60s mientras está mirando → "sigo acá" (respaldo para el navegador que muere de golpe
//     sin disparar ningún evento: sin esto quedaría marcado como "mirando" para siempre)
//   - en `pageshow` → el estado de nuevo, por si volvió del bfcache (el `pagehide` de la ida ya
//     reportó "me fui" y frenó el latido)
//   - al iniciar sesión → el estado de nuevo, ahora sí con token
//   - al cerrar sesión (o al entrar OTRA cuenta encima) → "me fui" por el que se va, con su token
//     mientras todavía sirve
//
// `pagehide` y no `beforeunload`: en iOS/Safari `beforeunload` no dispara de forma confiable, y
// `pagehide` además cubre el pase al bfcache (que para nosotros es lo mismo que irse).

import { useEffect } from 'react';
import {
  HEARTBEAT_MS,
  recordarToken,
  reportarPresencia,
  reportarSalida,
  seFueElUsuarioAnterior,
} from '../lib/presencia';
import { supabase } from '../lib/supabase';
import { escucharMirando, estaMirandoAhora } from '../lib/mirando';

export function usePresencia(): void {
  useEffect(() => {
    let intervalo: ReturnType<typeof setInterval> | null = null;

    const frenarHeartbeat = () => {
      if (intervalo) {
        clearInterval(intervalo);
        intervalo = null;
      }
    };

    const arrancarHeartbeat = () => {
      frenarHeartbeat(); // nunca dos intervalos a la vez
      // Cada latido mide el estado real en vez de repetir "sigo acá" a ciegas: si algún evento de
      // foco no llegó (pasa con algunos cambios de ventana), el latido lo corrige.
      intervalo = setInterval(() => {
        const mirando = estaMirandoAhora();
        void reportarPresencia(mirando);
        if (!mirando) frenarHeartbeat();
      }, HEARTBEAT_MS);
    };

    const sincronizar = () => {
      const mirando = estaMirandoAhora();
      void reportarPresencia(mirando);
      if (mirando) arrancarHeartbeat();
      else frenarHeartbeat(); // se fue (otra pestaña u otra app): no tiene sentido decir "sigo acá"
    };

    const alIrse = () => {
      frenarHeartbeat();
      // Síncrono: acá no hay tiempo para ningún `await` (ver `reportarSalida`).
      reportarSalida();
    };

    // Volver del back-forward cache (el botón "atrás"): el documento se restaura tal cual, sin
    // remontar nada, pero el `pagehide` de la ida ya reportó "me fui" y frenó el latido. Sin esto
    // la pestaña quedaba marcada como ausente mientras la persona la está mirando.
    const alVolver = (e: PageTransitionEvent) => {
      if (e.persisted) sincronizar();
    };

    sincronizar();
    const dejarDeEscuchar = escucharMirando(() => sincronizar());
    window.addEventListener('pagehide', alIrse);
    window.addEventListener('pageshow', alVolver);

    // Al entrar por la ruta pública y loguearse, `App` NO se vuelve a montar: el primer reporte ya
    // salió sin sesión (y sin token no se manda nada), así que sin esto la persona figuraba como
    // ausente hasta el siguiente latido y podía comerse un push mirando la pantalla (hallazgo de
    // Greptile, PR #83). Reportar de más es inofensivo: es idempotente.
    let usuarioActual: string | null = null;

    const { data: auth } = supabase.auth.onAuthStateChange((evento, sesion) => {
      // El token y el usuario salen del propio evento: pedirle la sesión a supabase adentro de su
      // callback puede trabar a supabase-js.
      const usuarioNuevo = sesion?.user?.id ?? null;

      // Despedir al anterior ANTES de pisar el token, mientras el suyo todavía sirve. Cubre el
      // cierre de sesión y el cambio de cuenta sin cierre: en los dos, esa persona quedaría
      // marcada como "mirando" hasta vencer, y en esa ventana se le come el push.
      if (seFueElUsuarioAnterior(evento, usuarioActual, usuarioNuevo)) {
        frenarHeartbeat();
        reportarSalida();
        recordarToken(null);
      }

      usuarioActual = usuarioNuevo;
      const token = sesion?.access_token ?? null;
      recordarToken(token);
      if (!token) return;
      const mirando = estaMirandoAhora();
      void reportarPresencia(mirando, { token });
      if (mirando) arrancarHeartbeat();
    });

    return () => {
      dejarDeEscuchar();
      window.removeEventListener('pagehide', alIrse);
      window.removeEventListener('pageshow', alVolver);
      auth.subscription.unsubscribe();
      frenarHeartbeat();
    };
  }, []);
}
